<script setup lang="ts">
    // @ts-nocheck
    // nocheck pga att vi har urgamla versioner av allt och TS kaosar

    /**
     * Struktur i komponenten:
     * inputValue är kopplat till formulateinput och är det värde som är synkat med formuläret.
     *
     * pickerValue är värdet på datum/tidsväljaren som renderas i poppern (PrimeDatePicker). Det är alltid ett js Date.
     *
     * inputMaskValue är kopplat till det visuella fältet som användaren matar in data i (PrimeInputMask).
     * Det är vid ändringar här som inputValue förändras, om det är en giltig tid/datum.
     * Vid giltiga delvis tid/datum kommer pickerValue uppdateras.
     */

    import KButton from "@/components/Button.vue";
    import { FormComponentType } from "@/components/forms/Form/convertFormData";
    import DateCell from "@/components/forms/inputs/DateTimepicker/DateCell.vue";
    import { createDateTimeValidation } from "@/components/forms/inputs/DateTimepicker/dateTimeValidation";
    import { useInputHelpers } from "@/components/forms/reusables/useInputHelpers";
    import { useValidation } from "@/components/forms/reusables/useValidation";
    import KLabel from "@/components/forms/subComponents/Label/KLabel.vue";
    import KSuffix from "@/components/forms/subComponents/Suffix/KSuffix.vue";
    import moment from "moment/moment";
    import { computed, inject, nextTick, onMounted, ref, toRefs, watch } from "vue";

    const dateMask = "åååå-mm-dd";
    const timeMask = "tt:mm";
    const dateFormat = "yyyy-MM-DD";
    const timeFormat = "HH:mm";
    const datetimeFormat = `${dateFormat} ${timeFormat}`;

    // TODO Använd CommonProps i types i Vue3
    interface Props {
        label?: string;
        /**
         * Skicka INTE med "date"
         */
        validation?: string | Array<[rule: string, ...args: never]>;
        name: string;
        disabled?: boolean;
        /**
         * Typ av picker
         */
        type?: "date" | "time";
        /**
         * Ska år visas? Om det inte visas kommer det sättas automatiskt under ytan
         */
        showYear?: boolean;
        /**
         * Min tillåtna datum
         */
        minDate?: string | number | Date;
        /**
         * Max tillåtna datum
         */
        maxDate?: string | number | Date;
        /**
         * Datum som ska markeras på något sätt i kalendern
         */
        markedDates?: Array<{
            date: string;
            type: "block" | "warn";
        }>;
        /**
         * Formatet på value vid submit samt vid emit av inputValue. Default är datetime (yyyy-mm-dd hh:mm).
         * date är utan tid.
         * time är utan datum.
         * jsDate är Date
         */
        convertTo?: "datetime" | "date" | "time" | "jsDate";
        readonly?: boolean;
        /**
         * Ska värdet på fältet tas bort vid submit?
         */
        ignoreValue?: boolean;
        /**
         * Human readable-namn som ska visas vid error
         */
        validationName?: string;
        /**
         * KSub.vue prop Text som renderas under label
         */
        subText?: string;
        /**
         * KSub.vue prop Ikon som renderas under label
         */
        subIcon?: string;
        /**
         * KSub.vue prop Typ av subtitle
         */
        subType?: string;
        /**
         * KSuffix.vue prop Text som renderas under input
         */
        suffixText?: string;
        /**
         * KSuffix.vue prop Ikon som renderas under input
         */
        suffixIcon?: string;
        /**
         * KSuffix.vue prop Typ av subtitle
         */
        suffixType?: "info" | "info-dark" | "note";
        /**
         * Ska knappen för sättning av nuvarande tid visas? Renderas bredvid input
         */
        showNowButton?: boolean;
        /**
         * Funktion som returnerar förändrade värdet vid submit
         */
        converter?: ({ value }: { value: string | null }) => string | Date | null;
        border?: boolean;
        variant?: "blue-background" | "transparent";
        layout?: "default" | "compact";
        ignoreEmptyData?: boolean;
        disabledDates?: Array<Date>; // Gör enskilda datum disabled. (disabledDates på PrimeDatepicker)
        disabledDays?: Array<number>; // Gör specifika dagar i veckan disabled. (0 = söndag, 6 = lördag)(PrimeDatepicker)
        /**
         * Ska fältet vara synligt? Override för inställning av synlighet som görs via config
         */
        shouldBeVisible?: boolean;
        /**
         * Initialvärde. Används t.ex. inuti KRepeater där VueFormulates group-kontext
         * inte propagerar initialvärdet via @input. Fungerar som KAutocompletes value-prop.
         */
        value?: string | null;
    }

    const props = withDefaults(defineProps<Props>(), {
        type: "date",
        disabled: false,
        ignoreValue: false,
        showYear: false,
        showNowButton: undefined,
        validation: "kdate",
        border: false,
        ignoreEmptyData: false,
        shouldBeVisible: undefined,
        layout: "default",
    });

    const {
        name,
        disabled,
        type,
        readonly,
        ignoreValue,
        validation,
        markedDates,
        converter,
        convertTo,
        maxDate,
        minDate,
        suffixText,
        suffixType,
        ignoreEmptyData,
        disabledDates,
        disabledDays,
        shouldBeVisible,
        value,
    } = toRefs(props);

    const emit = defineEmits<{
        (event: "inputValue", payload: string | Date | null): void;
    }>();

    /**
     * Referens till pickern, inte input (i det här fallet Primevues Calendar/Datepicker)
     */
    const pickerRef = ref();
    /**
     * Värdet i pickern (Date)
     */
    const pickerValue = ref();
    /**
     * Värdet i input. Det är det som kommer hämtas upp av formuläret
     */
    const inputValue = ref(value?.value ?? undefined);
    /**
     * Värdet i input mask. Det är det som kommer manipuleras och sättas som inputValue
     */
    const inputMaskValue = ref();
    /**
     * Ref till input
     */
    const inputRef = ref();
    /**
     * Om popper ska visas.
     */
    const showPopper = ref(false);
    /**
     * Referens till formulate input. Används för att anropa blur handler och validering
     */
    const formulateRef = ref();

    const configNames = inject<{
        moduleName: string;
        subModuleName: string;
    }>("configNames");

    const { modifiedValidation, expose: useValidationExpose } = useValidation({
        name: name.value,
        validation: createDateTimeValidation({ baseValidation: validation?.value }),
        moduleName: configNames?.moduleName,
        subModuleName: configNames?.subModuleName,
    });
    const {
        modifiedDisabled,
        modifiedReadonly,
        modifiedSuffix,
        modifiedSuffixType,
        shouldBeVisible: internalShouldBeVisible,
        expose: useInputHelpersExpose,
    } = useInputHelpers({
        configNames,
        shouldBeVisible,
        name: name.value,
        kType: FormComponentType.Date,
        disabled: disabled?.value,
        readonly: readonly?.value,
        suffixText: suffixText?.value,
        suffixType: suffixType?.value,
        ignoreValue: ignoreValue?.value,
        ignoreEmptyData: ignoreEmptyData?.value,
        convertData: ({ value }: { value: string | null }) => {
            if (converter?.value) {
                return converter.value({ value });
            }

            if (!value) {
                return null;
            }

            return convertToValue(value);
        },
    });

    const emitInputValue = ($event: Event) => {
        const value = $event ? convertToValue($event) : null;
        emit("inputValue", value);
    };

    const convertToValue = (value) => {
        // Antar att om ingen convertTo satts och type är tid att tid är formatet som förväntas tillbaka
        if (!convertTo?.value && type.value === "time") {
            return moment(value).format(timeFormat);
        }

        // Antar att om ingen convertTo satts och type är datum att datum är formatet som förväntas tillbaka
        if (!convertTo?.value && type.value === "date") {
            return moment(value).format(dateFormat);
        }

        if (convertTo?.value === "datetime") {
            return moment(value).format(datetimeFormat);
        }

        if (convertTo?.value === "date") {
            return moment(value).format(dateFormat);
        }

        if (convertTo?.value === "time") {
            return moment(value).format(timeFormat);
        }

        if (convertTo?.value === "jsDate") {
            return moment(value).toDate();
        }

        return value;
    };
    /**
     * Kolla om datumet ingår i skickade samlingen av "markerade" datum och returnerar dess data
     */
    const getMarkedDate = ({ date }: { date: { day: number; year: number; month: number } }) => {
        return markedDates?.value?.find((marked) =>
            moment(marked.date).isSame(
                moment({
                    year: date.year,
                    month: date.month,
                    day: date.day,
                })
            )
        );
    };

    /**
     * Validerar att input masks value är OK
     */
    const validateInputMask = () => {
        // Om input innehåller minst 1 siffra men inputvalue inte är satt (vilket betyder att ett giltigt datum satts än
        if (inputMaskValue.value?.match?.(/[0-9]/) && !inputValue.value) {
            return false;
        }

        if (type.value === "time" && !inputMaskValue.value?.match?.(/^[0-9]{2}:[0-9]{2}$/)) {
            return false;
        }

        if (type.value === "date" && !inputMaskValue.value?.match?.(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/)) {
            return false;
        }

        return true;
    };

    /**
     * Validerar att value som skickas in är OK
     */
    const validateValue = ({ value }: { value?: string }) => {
        return (
            (value?.match?.(/^[0-9]{4}-[0-9]{2}-[0-9]{2} [0-9]{2}:[0-9]{2}$/) ||
                value?.match?.(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/)) &&
            moment(value).isValid()
        );
    };

    /**
     * Validerar att input innehåller ett giltigt datetime och att input mask är en giltig tid/datum.
     * Eftersom värdet i input inte ändras vid felaktiga tider/datum kommer det bara ändras om det är giltigt
     * datum/tid i input mask. Därför valideras båda
     */
    const validateDate = ({ value }: { value?: string }) => {
        // Om inget värde är satt och inget matats in i input. Vi låter inbyggda required check kolla tomma fält
        if (!inputMaskValue.value && !value) {
            return true;
        }

        if (!validateInputMask()) {
            setInputValue({ date: null });

            return false;
        }

        /*
                Validerar baserat på tecken, att value är ett giltigt datum och att input mask är ett giltigt datum.
                Det sista behövs i kombination med ovan för att säkerställa att det innehåller rätt tecken OCH är ett
                giltigt datum. moment tolkar annars "2025-07-dd" som giltigt
             */
        return (
            validateValue({ value }) &&
            moment(inputMaskValue.value, type.value === "time" ? timeFormat : dateFormat).isValid()
        );
    };

    const validateMinDate = ({ value }: { value?: string }) => {
        return (
            !value ||
            !minDate?.value ||
            moment(inputMaskValue.value, type.value === "time" ? timeFormat : dateFormat).isSameOrAfter(
                moment(minDate.value, type.value === "time" ? "minute" : "day"),
                type.value === "time" ? "minute" : "day"
            )
        );
    };

    const validateMaxDate = ({ value }: { value?: string }) => {
        return (
            !value ||
            !maxDate?.value ||
            moment(inputMaskValue.value, type.value === "time" ? timeFormat : dateFormat).isSameOrBefore(
                moment(maxDate.value, type.value === "time" ? timeFormat : dateFormat),
                type.value === "time" ? "minute" : "day"
            )
        );
    };

    /**
     * Vid klick på "rensa" eller kryss
     * forceValidation true gör att validation görs oavsett om pickern syns eller inte. Behövs i fallet när
     * kryss klickas på, då pickern kan vara dold
     */

    const onClear = async ({ forceValidation }: { forceValidation?: boolean }) => {
        inputValue.value = null;
        inputMaskValue.value = null;
        pickerValue.value = null;

        if (forceValidation) {
            formulateRef.value?.context?.blurHandler?.();
            await formulateRef.value?.context?.performValidation?.();
        }

        await hide();
    };

    /**
     * När poppern visas.
     */

    const onShow = () => {
        // Behövs för att låta input mask field ha kvar fokus efter att popper med calendar renderats
        setTimeout(() => {
            if (type.value === "date") {
                inputRef.value.$el.focus();
                pickerRef.value.$parent.$children[0].focused = true;
            }
        }, 100);
    };

    /**
     * Funktion som anropas när poppern ska döljas (t.ex blur, tab)
     */
    const hide = async () => {
        if (!showPopper.value) {
            return;
        }

        showPopper.value = false;

        autocomplete();

        formulateRef.value?.context?.blurHandler?.();
        await formulateRef.value?.context?.performValidation?.();
    };

    /**
     * Autocompletar input (om tid är HH: i mask)
     */
    const autocomplete = () => {
        if (type.value === "time" && inputMaskValue.value?.match(/^[0-9]{2}:mm/)) {
            inputMaskValue.value = `${inputMaskValue.value.split(":")[0]}:00`;
        }
    };

    const onFocus = () => {
        showPopper.value = true;
    };

    /**
     * Lyssnar på Enter, Tab och Escape för att avgöra när popper ska stängas. Behövs manuell lyssning pga
     * att det inte går att styra exakt när poppern ska visas/döljas
     */
    const onKeydown = (event: KeyboardEvent) => {
        if (event.key === "Enter" || event.key === "Tab" || event.key === "Escape") {
            hide();
        }
    };

    /**
     * Sätter värdet på input som är kopplat till formuläret.
     * Sättning görs via watch på inputMaskValue. Du vill förmodligen INTE anropa den här direkt
     */

    const setInputValue = ({ date }: { date: Date | string | null }) => {
        if (!date) {
            inputValue.value = null;

            return;
        }

        inputValue.value = moment(date).format(datetimeFormat);
    };

    /**
     * Sätter det visuella värdet (som även valideras). Hämtas inte av formuläret
     */

    const setInputMaskValue = ({ date }: { date: Date | string | null }) => {
        if (!date) {
            inputMaskValue.value = null;
        } else if (type.value === "date") {
            inputMaskValue.value = moment(date).format(dateFormat);
        } else if (type.value === "time") {
            inputMaskValue.value = moment(date).format(timeFormat);
        }

        // Behövs för att triggra rerender vid första sättning
        inputRef.value.checkVal();
    };

    /**
     * Sätt dagens datum + nuvarande tid
     */

    const onNow = async () => {
        const newDate = moment().toDate();

        setInputMaskValue({ date: newDate });

        await nextTick();

        if (!showPopper.value) {
            await formulateRef.value?.context?.performValidation?.();
        }

        await hide();
    };

    /**
     * Vid val av datum. Anropas vid val av datum i datumväljaren.
     * Anropas även i tidväljaren vid ändring av tid. Vi ser till att INTE dölja poppern om type är time
     */

    const onDateSelect = async (newDate: Date) => {
        setInputMaskValue({ date: newDate });

        if (type.value !== "time") {
            // Behövs för att validering i hide inte ska ske för tidigt och rensa värden
            await nextTick();

            await hide();
        }
    };

    defineExpose({
        ...useValidationExpose,
        ...useInputHelpersExpose,
        setValue: onDateSelect,
    });

    /**
     * Kollar på klick utanför input och popper. Popper ska stängas om klick gjorts utanför dem.
     */
    const onClickOutsideHandler = (
        event: Event & { target: (Event["target"] & { matches: (selector: string) => boolean }) | null }
    ) => {
        /*
         * Den här enorma mängden selectors behövs för att kunna targetta rätt i Primevues komponent. Vid klick på
         * vissa knappar räcker det inte med att bara kolla på hela containern
         */
        const selectors = [
            `.k-date-timepicker-${name.value}`,
            ".p-calendar",
            ".p-datepicker-header",
            ".p-datepicker-year",
            ".p-yearpicker",
            ".p-monthpicker",
            ".p-datepicker-month",
            ".p-yearpicker-year",
        ];

        if (event?.target?.matches?.(selectors.map((selector) => `${selector}, ${selector} *`).join(", "))) {
            return;
        }

        hide();
    };

    const getValidDate = ({ value }: { value?: string }) => {
        return value?.match?.(/^[0-9]{4}-[0-9]{2}-[0-9]{2}/) ? moment(value).toDate() : moment().toDate();
    };

    /**
     * Lyssnar på ändringar på värde kopplat till formuläret och sätter input mask och pickerns värde baserat på det,
     * om inte input mask redan är satt. Kommer triggras vid inläsning av existerande data
     */
    watch(
        inputValue,
        (newValue, oldValue) => {
            if ((!newValue && !oldValue) || newValue === null) {
                /**
                 * Resettar input mask. Om både gamla och nya värdet inte är satt betyder det att det rensats och då borde
                 * inget vara kvar i input mask
                 */
                if (inputMaskValue.value) {
                    setInputMaskValue({ date: null });
                }

                return;
            }

            /**
             * Om input mask är satt, nya värdet är satt och det nya värdet inte validerar.
             */
            if (inputMaskValue.value && newValue && !validateValue({ value: newValue })) {
                setInputValue({ date: null });

                /**
                 * Om input mask är satt och gamla värdet är valid. Det betyder att värdet ändrats från något giltigt till
                 * något icke-giltigt och borde då rensa input mask. Händer när null/undefined skickas in utifrån (t.ex
                 * vid reset av formulärdata)
                 */
                if (validateInputMask() && validateValue({ value: oldValue })) {
                    setInputMaskValue({ date: null });
                }

                return;
            } else if (inputMaskValue.value && validateValue({ value: newValue })) {
                /**
                 * Om input mask är satt och nya värdet är giltigt. Då uppdaterar vi input mask
                 */
                setInputMaskValue({ date: newValue });
            } else if (!inputMaskValue.value) {
                if (typeof newValue === "string" && newValue?.match(/^[0-9]{2}:[0-9]{2}/)) {
                    const newDate = moment()
                        .set({
                            hours: Number(newValue.split(":")[0]),
                            minutes: Number(newValue.split(":")[1]),
                        })
                        .toDate();

                    setInputMaskValue({ date: newDate });

                    return;
                }

                setInputMaskValue({ date: newValue });
            }
        },
        { immediate: true }
    );

    /**
     * Sättning av värdet i picker och/eller input baserat på inmatat värde i input mask.
     * För picker är det primärt för att hålla den synkad med det som matats in av användaren. För input används
     * det för att sätta datum/tid när användaren matat in en giltigt datum/tid
     */
    watch(inputMaskValue, (newValue, oldValue) => {
        if (type.value === "time" && newValue?.match(/^[0-9]{2}:[0-9]{2}/) && moment(newValue, timeFormat).isValid()) {
            /**
             * Om det är klockslag och matchar HH:mm. Då omvandlar vi det till ett giltigt datum och sätter. Datumet baseras
             * på existerande datum i pickern eller dagens
             */

            const newDate = moment(getValidDate({ value: inputValue.value }))
                .set({
                    hours: Number(newValue.split(":")[0]),
                    minutes: Number(newValue.split(":")[1]),
                })
                .toDate();

            pickerValue.value = newDate;
            setInputValue({ date: newDate });
        } else if (
            type.value === "time" &&
            newValue?.match(/^[0-9]{2}/) &&
            moment(newValue.split(":")[0], "HH").isValid()
        ) {
            /**
             * Om inmatning börjar med timmar. Det är bara pickerValue som ändras här, för att synka det som
             * användaren skriver in i input mask med det som visas i popper
             */

            const newDate = moment(getValidDate({ value: inputValue.value })).set({
                hours: Number(newValue.split(":")[0]),
                minutes: 0,
            });

            pickerValue.value = newDate.toDate();
        } else if (
            type.value === "date" &&
            newValue?.match(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/) &&
            moment(newValue, dateFormat).isValid()
        ) {
            /**
             * Om inmatning är ett giltigt datum. Då sätts värdet i input och picker. Timmar och minuter baseras på
             * existerande värde i picker eller Date.now
             */

            const newDate = moment(newValue)
                .set({
                    hours: getValidDate({ value: inputValue.value })?.getHours?.() ?? pickerValue.value?.getHours?.(),
                    minutes:
                        getValidDate({ value: inputValue.value })?.getMinutes?.() ?? pickerValue.value?.getMinutes?.(),
                })
                .toDate();

            pickerValue.value = newDate;
            setInputValue({ date: newDate });
        } else if (
            type.value === "date" &&
            newValue?.match(/^[0-9]{4}-[0-9]{2}/) &&
            moment(newValue, "yyyy-MM").isValid()
        ) {
            /**
             * Om inmatning börjar med giltigt år och månader.
             * Det är bara pickerValue som ändras här, för att synka det som användaren skriver in
             * i input mask med det som visas i popper
             */

            const newDate = moment(getValidDate({ value: inputValue.value })).set({
                years: newValue.split("-")[0],
                months: newValue.split("-")[1],
            });

            pickerValue.value = newDate.toDate();
        } else if (type.value === "date" && newValue?.match(/^[0-9]{4}/)) {
            /**
             * Om inmatning börjar med giltigt år.
             * Det är bara pickerValue som ändras här, för att synka det som användaren skriver in
             * i input mask med det som visas i popper
             */

            const newDate = moment().set({ years: newValue.split("-")[0] });

            pickerValue.value = newDate.toDate();
        } else {
            pickerValue.value = moment().toDate();
        }
    });

    onMounted(() => {
        document.addEventListener("click", onClickOutsideHandler as any);
    });
</script>
<template>
    <!-- Inputen kopplad till formuläret -->
    <FormulateInput
        v-if="internalShouldBeVisible"
        ref="formulateRef"
        v-model="inputValue"
        @input="emitInputValue"
        :name="name"
        type="datetime-local"
        :class="[
            'k-date-timepicker',
            `k-date-timepicker-${name}`,
            { 'show-now': typeof showNowButton === 'boolean' ? showNowButton : type === 'time' },
            { 'field-container': border, [variant]: border },
            `layout-${layout}`,
        ]"
        :disabled="modifiedDisabled"
        :readonly="modifiedReadonly"
        :validationName="validationName ?? label"
        :validation="modifiedValidation"
        :validationRules="{
            kdate: validateDate,
            kdatemax: validateMaxDate,
            kdatemin: validateMinDate,
        }"
        :validationMessages="{
            kdate: ({ name }) => `${name} är inte ${type === 'date' ? 'ett giltigt datum' : 'en giltig tid'}`,
            kdatemax: ({ name }) => `${name} får var senast ${moment(maxDate).format(dateFormat)}`,
            kdatemin: ({ name }) => `${name} får vara tidigast ${moment(minDate).format(dateFormat)}`,
        }"
        :disableErrors="showPopper"
    >
        <template #label>
            <KLabel
                v-if="label"
                :content="label"
                :validation="modifiedValidation"
                :subText="subText"
                :subIcon="subIcon"
                :subType="subType"
            />
        </template>
        <template #element>
            <!-- Popper wrapper -->
            <VDropdown
                no-auto-focus
                :auto-hide="false"
                :triggers="[]"
                :show-triggers="[]"
                :hide-triggers="[]"
                :popper-triggers="[]"
                :popper-hide-triggers="[]"
                :popper-show-triggers="[]"
                eager-mount
                :shown="showPopper"
                @apply-show="onShow"
                class="picker"
            >
                <template #default>
                    <div class="wrapper">
                        <div class="input-container">
                            <!-- Input mask som används för visuella värdet -->
                            <PrimeInputMask
                                v-model="inputMaskValue"
                                ref="inputRef"
                                :mask="type === 'date' ? '9999-99-99' : '99:99'"
                                :autoClear="false"
                                :slotChar="type === 'date' ? dateMask : timeMask"
                                :placeholder="type === 'date' ? dateMask : timeMask"
                                @blur="autocomplete"
                                @focus="onFocus"
                                @keydown="onKeydown"
                                :disabled="modifiedDisabled"
                            />
                            <KButton
                                v-if="inputMaskValue"
                                type="button"
                                variant="hollow"
                                size="adaptable"
                                @click="onClear({ forceValidation: true })"
                                class="inline-clear-button"
                            >
                                <img src="@/assets/close_grey.svg" alt="Rensa" />
                            </KButton>
                        </div>
                        <KButton
                            v-if="typeof showNowButton === 'boolean' ? showNowButton : type === 'time'"
                            type="button"
                            size="adaptable"
                            class="now-button"
                            @click="onNow"
                        >
                            <img src="@/assets/time_white_transparent.svg" alt="arrow image" />
                        </KButton>
                    </div>
                </template>
                <template #popper>
                    <!-- Val av tid eller datum -->
                    <PrimeDatepicker
                        inline
                        showWeek
                        showButtonBar
                        ref="pickerRef"
                        v-model="pickerValue"
                        hourFormat="24"
                        view="date"
                        dateFormat="yy-mm-dd"
                        @today-click="onNow"
                        @clear-click="onClear"
                        @date-select="onDateSelect"
                        :timeOnly="type === 'time'"
                        :showTime="false"
                        :minDate="minDate ? moment(minDate).toDate() : undefined"
                        :maxDate="maxDate ? moment(maxDate).toDate() : undefined"
                        :disabledDates="disabledDates"
                        :disabledDays="disabledDays"
                    >
                        <template #date="slotProps">
                            <!-- Rendering av enskilda datumceller -->
                            <DateCell :content="slotProps.date.day" :data="getMarkedDate({ date: slotProps.date })" />
                        </template>
                    </PrimeDatepicker>
                </template>
            </VDropdown>
        </template>
        <template #help>
            <KSuffix class="span-all" :subText="modifiedSuffix" :subIcon="suffixIcon" :subType="modifiedSuffixType" />
        </template>
    </FormulateInput>
</template>

<style lang="scss">
    @import "@/style/form";
    @import "@/style/variables";
    @import "@/components/forms/Form/styles/FieldBackgrounds.scss";

    .field-container {
        // Om ingen variant är satt, används inget bakgrundsfärg
        @include field-container-mixin();
    }

    .v-popper__popper {
        &:has(.p-calendar) {
            width: 22rem;
            max-width: 100%;
        }

        .p-calendar {
            width: 100%;
            height: 100%;

            .p-datepicker {
                table td {
                    padding: 0;
                }

                table td > span {
                    border-radius: $border-radius;
                }

                .p-datepicker-buttonbar {
                    padding: 0;
                    padding-top: 0.5rem;
                }

                .p-datepicker-weekheader,
                .p-datepicker-weeknumber {
                    text-align: center;
                    font-size: 0.9rem;
                    opacity: 0.6;
                }

                thead {
                    th > span {
                        height: unset;
                    }
                }

                .p-datepicker-title {
                    .p-datepicker-month {
                        margin-right: 0;
                    }
                }

                &.p-datepicker-timeonly {
                    // Gömmer default label på "Idag" och sätter det till "Nu" när det bara är en tidväljare utan datum
                    .p-datepicker-buttonbar {
                        button:first-child {
                            .p-button-label {
                                visibility: hidden;
                            }

                            .p-button-label::after {
                                content: "Nu";
                                visibility: visible;
                                position: absolute;
                                top: 0;
                                left: 0;
                                height: 100%;
                                width: 100%;
                                display: grid;
                                place-items: center;
                            }
                        }
                    }
                }
            }
        }
    }
</style>

<style scoped lang="scss">
    @import "@/style/form";
    @import "@/style/variables";

    .k-date-timepicker {
        ::v-deep .p-inputmask {
            font-family: Roboto, sans-serif;
            position: relative;
            height: $form-input-height-default;
            padding: $form-input-default-padding;
            border: $input-border;
            width: 100%;
            font-size: 16px;
        }

        .input-container {
            height: 100%;
            position: relative;

            .inline-clear-button {
                position: absolute;
                right: 0;
                top: $form-input-default-padding-top;
                padding: 0.2rem;
                margin-right: 0.7rem;
                bottom: $form-input-default-padding-bottom;
                margin-top: auto;
                margin-bottom: auto;
                height: fit-content;
            }
        }

        &.show-now {
            .wrapper {
                display: grid;
                grid-auto-flow: column;
                grid-template-columns: 1fr auto;
            }

            ::v-deep .p-inputmask {
                border-right: none;
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
            }

            .now-button {
                border: $input-border;
                border-top-left-radius: 0;
                border-bottom-left-radius: 0;
                border-left: none;
                width: 2.5rem;
            }

            &[data-is-showing-errors="true"] {
                .now-button {
                    border-bottom: $input-border-error;
                }
            }
        }

        &[data-is-showing-errors="true"] {
            .input-container {
                input {
                    border-bottom: $input-border-error;
                }

                .inline-clear-button {
                    bottom: calc($form-input-default-padding-bottom + $input-border-error-height);
                }
            }

            .now-button {
                border-bottom: $input-border-error;
            }
        }

        &.layout-compact {
            .input-container {
                .inline-clear-button {
                    bottom: 0.9rem;
                }
            }
            ::v-deep .p-inputmask {
                font-size: 14px;
            }
            ::v-deep .formulate-input-wrapper {
                height: 100%;

                .formulate-input-element,
                .formulate-input-element > div,
                .mx-datepicker,
                .mx-input-wrapper,
                .v-popper,
                .v-popper .wrapper,
                input {
                    height: 100%;
                }

                input {
                    padding-top: 0;
                    padding-bottom: 0;
                }
            }
        }
    }
</style>
